// src/appRWAPromo/hooks/useMarketFeed.js
import { useEffect, useMemo, useState } from "react";
import { collection, limit, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../components/firebase";

function truthy(v) {
  return v === true || v === "true" || v === 1;
}

function normalizeStreamDoc(docSnap) {
  const data = docSnap.data() || {};
  return {
    id: docSnap.id,

    // ВАШ НОВЫЙ ФОРМАТ
    type: "stream",
    name: data.name || data.title || "Stream",
    handle: data.handle || data.subtitle || "",
    category: data.category || "Crypto",
    flags: data.flags || { isNew: false, isHot: false, isBreaking: false },

    // хранить можно, но карточка не покажет (мы это уже убрали в UnifiedMarketCard)
    marketChip: data.marketChip || "",
    marketSub: data.marketSub || "",

    avatarUrl: data.avatarUrl || "",
    poster: data.poster || "",
    videoSrc: data.videoSrc || data.url || data.hlsUrl || "",

    status: String(data.status || "record").toLowerCase(), // live | record
    views: Number(data.views || 0),
    likes: Number(data.likes || 0),
    shareUrl: data.shareUrl || "",
    previewSrc: data.previewSrc || "",
    autoplay: Boolean(data.autoplay ?? true),
    loop: Boolean(data.loop ?? true),
    controls: Boolean(data.controls ?? true),

    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,

    // для фильтров/поиска (если у вас Filters ищет market)
    market: data.market || data.marketChip || data.marketSub || null,
    tags: Array.isArray(data.tags) ? data.tags : [],
    assets: Array.isArray(data.assets) ? data.assets : [],
    description: data.description || "",
  };
}

function normalizePromoDoc(docSnap) {
  const data = docSnap.data() || {};
  const kind = String(data.kind || "").toLowerCase();

  // “prediction” у вас приходит либо как type="prediction", либо как forecast/isForecast
  const isPrediction =
    String(data.type || "").toLowerCase() === "prediction" ||
    truthy(data.isForecast) ||
    kind === "forecast";

  if (!isPrediction) return null;

  return {
    id: docSnap.id,

    // ВАШ НОВЫЙ ФОРМАТ
    type: "prediction",
    name: data.name || data.title || data.marketTitle || "Prediction",
    handle: data.handle || data.creatorHandle || data.company || "",
    category: data.category || data.topic || "Crypto",
    flags: data.flags || {
      isNew: truthy(data.isNew),
      isHot: truthy(data.isHot),
      isBreaking: truthy(data.isBreaking),
    },

    // хранить можно, но карточка не покажет
    marketChip: data.marketChip || "",
    marketSub: data.marketSub || "",

    avatarUrl: data.avatarUrl || data.avatar || "",
    poster: data.poster || "",
    imageSrc: data.imageSrc || (Array.isArray(data.images) ? data.images[0] : "") || "",

    status: String(data.status || "approved").toLowerCase(), // approved
    views: Number(data.views || 0),
    likes: Number(data.likes || 0),
    shareUrl: data.shareUrl || "",

    autoplay: Boolean(data.autoplay ?? false),
    loop: Boolean(data.loop ?? false),
    controls: Boolean(data.controls ?? false),

    isForecast: true,

    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,

    // для фильтров/поиска
    market: data.market || data.marketTag || data.section || data.marketChip || data.marketSub || null,
    tags: Array.isArray(data.tags) ? data.tags : [],
    assets: Array.isArray(data.assets) ? data.assets : [],
    description: data.description || "",
  };
}

function sortByTimeDesc(list) {
  // сортируем по updatedAt/createdAt если есть
  const getTs = (x) => {
    const t = x?.updatedAt || x?.createdAt;
    // Firestore Timestamp {seconds}
    if (t?.seconds) return t.seconds;
    // Date
    if (t instanceof Date) return Math.floor(t.getTime() / 1000);
    return 0;
  };
  return [...list].sort((a, b) => getTs(b) - getTs(a));
}

export function useMarketFeed({ pageSize = 300 } = {}) {
  const [streamsRaw, setStreamsRaw] = useState([]);
  const [predsRaw, setPredsRaw] = useState([]);

  const [loadingStreams, setLoadingStreams] = useState(true);
  const [loadingPredictions, setLoadingPredictions] = useState(true);

  useEffect(() => {
    setLoadingStreams(true);

    const qStreams = query(collection(db, "avatar_streams"), limit(pageSize));
    const unsub = onSnapshot(
      qStreams,
      (snap) => {
        const list = snap.docs.map(normalizeStreamDoc);
        setStreamsRaw(list);
        setLoadingStreams(false);
      },
      (err) => {
        console.error("avatar_streams snapshot error:", err);
        setStreamsRaw([]);
        setLoadingStreams(false);
      }
    );

    return () => unsub();
  }, [pageSize]);

  useEffect(() => {
    setLoadingPredictions(true);

    const qPromos = query(collection(db, "promos"), where("status", "==", "approved"), limit(pageSize));
    const unsub = onSnapshot(
      qPromos,
      (snap) => {
        const list = snap.docs.map(normalizePromoDoc).filter(Boolean);
        setPredsRaw(list);
        setLoadingPredictions(false);
      },
      (err) => {
        console.error("promos snapshot error:", err);
        setPredsRaw([]);
        setLoadingPredictions(false);
      }
    );

    return () => unsub();
  }, [pageSize]);

  const streams = useMemo(() => sortByTimeDesc(streamsRaw), [streamsRaw]);
  const predictions = useMemo(() => sortByTimeDesc(predsRaw), [predsRaw]);

  return { streams, predictions, loadingStreams, loadingPredictions };
}
